import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useMenu, useRsMutation, useTables, type MenuCategory, type MenuItem } from '../api/restaurant';
import { formatGhs, parseCedis } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { menuPhotoUrl, preparePhoto } from '../ui/photo';
import { CombosTab, ExtrasSection } from './MenuExtras';

type Tab = 'menu' | 'combos' | 'tables';

export function MenuManager() {
  const [tab, setTab] = useState<Tab>('menu');
  return (
    <div className="page">
      <PageHead eyebrow="Restaurant and bar" title="Menu and tables" />
      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'menu'} onClick={() => setTab('menu')}>Menu</button>
        <button type="button" aria-pressed={tab === 'combos'} onClick={() => setTab('combos')}>Combos</button>
        <button type="button" aria-pressed={tab === 'tables'} onClick={() => setTab('tables')}>Tables</button>
      </div>
      {tab === 'menu' ? <MenuTab /> : tab === 'combos' ? <CombosTab /> : <TablesTab />}
    </div>
  );
}

/* ------------------------------------------------------------------- menu */
function MenuTab() {
  const menu = useMenu(true);
  const [adding, setAdding] = useState(false);
  const [newCat, setNewCat] = useState(false);
  const [edit, setEdit] = useState<MenuItem | null>(null);
  const cats = menu.data ?? [];
  const editing = cats.flatMap((c) => c.items).find((i) => i.id === edit?.id) ?? null;

  return (
    <>
      <div className="row">
        <Button variant="primary" disabled={cats.length === 0} onClick={() => setAdding(true)}>Add a dish or drink</Button>
        <Button onClick={() => setNewCat(true)}>Add a category</Button>
      </div>
      {menu.isLoading ? (
        <Loading rows={3} />
      ) : menu.error ? (
        <ErrorNote error={menu.error} retry={() => void menu.refetch()} />
      ) : cats.length === 0 ? (
        <div className="card flat"><Empty icon="inbox" title="No menu yet">Start with a category such as Mains, Drinks or Breakfast.</Empty></div>
      ) : (
        cats.map((c) => (
          <section key={c.id} className="stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div className="eyebrow">{c.name}{c.active ? '' : ' (hidden)'}</div>
            </div>
            <div className="list">
              {c.items.length === 0 ? <p className="small muted">No items in this category.</p> : null}
              {c.items.map((i) => (
                <button key={i.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setEdit(i)}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="title">{i.name}</span>
                    <span className="row">
                      {!i.active ? <Badge>Hidden</Badge> : null}
                      {i.soldOut ? <Badge tone="bad">Sold out</Badge> : null}
                      {i.modifierGroups.length > 0 ? <Badge>Extras</Badge> : null}
                      <Badge>{i.station === 'BAR' ? 'Bar' : 'Kitchen'}</Badge>
                    </span>
                  </div>
                  <span className="small muted num">{i.variants.filter((v) => v.active).map((v) => (i.variants.length > 1 ? `${v.name} ${formatGhs(v.price)}` : formatGhs(v.price))).join(' · ') || 'No price'}</span>
                </button>
              ))}
            </div>
          </section>
        ))
      )}
      <CategoryDialog open={newCat} onClose={() => setNewCat(false)} />
      <NewItemDialog open={adding} onClose={() => setAdding(false)} cats={cats} />
      <EditItemDialog item={editing} onClose={() => setEdit(null)} />
    </>
  );
}

function CategoryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const toast = useToast();
  const report = useReport();
  const make = useRsMutation((n: string) => api('/menu/categories', { method: 'POST', body: { name: n } }));
  return (
    <Modal open={open} onClose={onClose} title="New category">
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (name.trim().length >= 2) make.mutate(name.trim(), { onError: report, onSuccess: () => { toast.ok('Category added'); setName(''); onClose(); } }); }}>
        <Field id="cn" label="Name"><input id="cn" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Breakfast" /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={name.trim().length < 2} loading={make.isPending}>Add</Button>
        </div>
      </form>
    </Modal>
  );
}

interface VariantDraft {
  name: string;
  price: string;
}

function NewItemDialog({ open, onClose, cats }: { open: boolean; onClose: () => void; cats: MenuCategory[] }) {
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [station, setStation] = useState<'KITCHEN' | 'BAR'>('KITCHEN');
  const [department, setDepartment] = useState<'FOOD' | 'BEVERAGE'>('FOOD');
  const [variants, setVariants] = useState<VariantDraft[]>([{ name: 'Regular', price: '' }]);
  const make = useRsMutation((body: Record<string, unknown>) => api('/menu/items', { method: 'POST', body }));
  useEffect(() => { if (open && !categoryId) setCategoryId(cats[0]?.id ?? ''); }, [open, cats, categoryId]);

  const parsed = variants.map((v) => ({ name: v.name.trim(), price: parseCedis(v.price) }));
  const valid = name.trim().length >= 2 && !!categoryId && parsed.every((v) => v.name && v.price !== null) && new Set(parsed.map((v) => v.name.toLowerCase())).size === parsed.length;

  return (
    <Modal open={open} onClose={onClose} title="Add a dish or drink">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate({ categoryId, name: name.trim(), station, department, variants: parsed }, { onError: report, onSuccess: () => { toast.ok(`${name.trim()} added`); setName(''); setVariants([{ name: 'Regular', price: '' }]); onClose(); } });
        }}
      >
        <Field id="in" label="Name"><input id="in" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <Field id="ic" label="Category">
          <select id="ic" className="select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <div className="grid-2">
          <Field id="is" label="Made at">
            <select id="is" className="select" value={station} onChange={(e) => { const s = e.target.value as 'KITCHEN' | 'BAR'; setStation(s); setDepartment(s === 'BAR' ? 'BEVERAGE' : 'FOOD'); }}>
              <option value="KITCHEN">Kitchen</option>
              <option value="BAR">Bar</option>
            </select>
          </Field>
          <Field id="id" label="Counts as" hint="Decides the tax and the revenue account">
            <select id="id" className="select" value={department} onChange={(e) => setDepartment(e.target.value as 'FOOD' | 'BEVERAGE')}>
              <option value="FOOD">Food</option>
              <option value="BEVERAGE">Drinks</option>
            </select>
          </Field>
        </div>
        <div className="eyebrow">Sizes and prices (GH₵, tax included)</div>
        {variants.map((v, i) => (
          <div key={i} className="grid-2">
            <input className="input" aria-label="Size name" value={v.name} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Size" />
            <div className="row">
              <input className="input" aria-label="Price" inputMode="decimal" value={v.price} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} placeholder="0.00" />
              {variants.length > 1 ? <Button type="button" variant="ghost" aria-label="Remove size" onClick={() => setVariants(variants.filter((_, j) => j !== i))}>✕</Button> : null}
            </div>
          </div>
        ))}
        {variants.length < 6 ? <Button type="button" variant="ghost" onClick={() => setVariants([...variants, { name: '', price: '' }])}>Add another size</Button> : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Add to menu</Button>
        </div>
      </form>
    </Modal>
  );
}

function PhotoSection({ item }: { item: MenuItem }) {
  const toast = useToast();
  const report = useReport();
  const [failed, setFailed] = useState(false);
  const upload = useRsMutation(async (file: File) => api(`/menu/items/${item.id}/photo`, { method: 'PUT', body: { data: await preparePhoto(file) } }));
  const remove = useRsMutation(() => api(`/menu/items/${item.id}/photo`, { method: 'DELETE' }));
  const has = item.photoVersion !== null && !failed;
  return (
    <div className="row" style={{ alignItems: 'center' }}>
      <span className="bar-photo" style={{ width: 88, height: 88, flex: 'none' }} aria-hidden="true">
        {has ? <img src={menuPhotoUrl(item.id, item.photoVersion!)} alt="" onError={() => setFailed(true)} /> : item.name.slice(0, 1).toUpperCase()}
      </span>
      <div className="stack" style={{ flex: 1 }}>
        <span className="small muted">{has ? 'This picture shows on the Bar sale and order screens.' : 'No picture yet. A clear, bright photo of the dish or drink works best.'}</span>
        <div className="row">
          <label className="btn" style={{ cursor: 'pointer' }}>
            {upload.isPending ? 'Saving…' : has ? 'Change photo' : 'Add photo'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label={`${has ? 'Change' : 'Add'} photo for ${item.name}`}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) upload.mutate(f, { onError: report, onSuccess: () => { setFailed(false); toast.ok('Photo saved'); } });
              }}
            />
          </label>
          {has ? <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(undefined, { onError: report, onSuccess: () => toast.ok('Photo removed') })}>Remove photo</Button> : null}
        </div>
      </div>
    </div>
  );
}

function EditItemDialog({ item, onClose }: { item: MenuItem | null; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [size, setSize] = useState('');
  const [sizePrice, setSizePrice] = useState('');
  const patchItem = useRsMutation((body: Record<string, unknown>) => api(`/menu/items/${item!.id}`, { method: 'PATCH', body }));
  const patchVariant = useRsMutation((v: { id: string; body: Record<string, unknown> }) => api(`/menu/variants/${v.id}`, { method: 'PATCH', body: v.body }));
  const addVariant = useRsMutation((body: Record<string, unknown>) => api(`/menu/items/${item!.id}/variants`, { method: 'POST', body }));
  useEffect(() => {
    setPrices(Object.fromEntries((item?.variants ?? []).map((v) => [v.id, (v.price / 100).toFixed(2)])));
    setSize('');
    setSizePrice('');
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={!!item} onClose={onClose} title={item?.name ?? 'Item'}>
      {item ? (
        <div className="stack">
          <PhotoSection item={item} />
          <p className="small muted">Price changes apply to new orders. Orders already taken keep the price they were sold at.</p>
          {item.variants.map((v) => {
            const p = parseCedis(prices[v.id] ?? '');
            const changed = p !== null && p !== v.price;
            return (
              <div key={v.id} className="row" style={{ alignItems: 'flex-end' }}>
                <Field id={`vp-${v.id}`} label={`${v.name}${v.active ? '' : ' (hidden)'} (GH₵)`}>
                  <input id={`vp-${v.id}`} className="input" inputMode="decimal" value={prices[v.id] ?? ''} onChange={(e) => setPrices({ ...prices, [v.id]: e.target.value })} />
                </Field>
                <Button disabled={!changed} loading={patchVariant.isPending} onClick={() => patchVariant.mutate({ id: v.id, body: { price: p! } }, { onError: report, onSuccess: () => toast.ok('Price updated') })}>Save</Button>
                {item.variants.length > 1 ? (
                  <Button variant="ghost" onClick={() => patchVariant.mutate({ id: v.id, body: { active: !v.active } }, { onError: report })}>{v.active ? 'Hide' : 'Show'}</Button>
                ) : null}
              </div>
            );
          })}
          <form
            className="row"
            style={{ alignItems: 'flex-end' }}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const p = parseCedis(sizePrice);
              if (size.trim() && p !== null) addVariant.mutate({ name: size.trim(), price: p }, { onError: report, onSuccess: () => { toast.ok('Size added'); setSize(''); setSizePrice(''); } });
            }}
          >
            <Field id="ns" label="New size"><input id="ns" className="input" value={size} onChange={(e) => setSize(e.target.value)} placeholder="e.g. Large" /></Field>
            <Field id="np" label="Price"><input id="np" className="input" inputMode="decimal" value={sizePrice} onChange={(e) => setSizePrice(e.target.value)} /></Field>
            <Button type="submit" disabled={!size.trim() || parseCedis(sizePrice) === null} loading={addVariant.isPending}>Add</Button>
          </form>
          <ExtrasSection item={item} />
          <div className="row">
            <Button onClick={() => patchItem.mutate({ active: !item.active }, { onError: report, onSuccess: () => toast.ok(item.active ? 'Hidden from the menu' : 'Back on the menu') })}>{item.active ? 'Hide from menu' : 'Show on menu'}</Button>
          </div>
          <Button block onClick={onClose}>Done</Button>
        </div>
      ) : null}
    </Modal>
  );
}

/* ----------------------------------------------------------------- tables */
function TablesTab() {
  const tables = useTables();
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [area, setArea] = useState('');
  const [capacity, setCapacity] = useState('4');
  const add = useRsMutation((body: Record<string, unknown>) => api('/tables', { method: 'POST', body }));
  const retire = useRsMutation((id: string) => api(`/tables/${id}`, { method: 'PATCH', body: { active: false } }));

  return (
    <>
      <form
        className="stack card"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!name.trim()) return;
          add.mutate({ name: name.trim(), ...(area.trim() ? { area: area.trim() } : {}), capacity: Math.max(1, Number(capacity) || 4) }, { onError: report, onSuccess: () => { toast.ok(`Table ${name.trim()} added`); setName(''); } });
        }}
      >
        <div className="eyebrow">Add a table</div>
        <div className="grid-2">
          <Field id="tn" label="Name or number"><input id="tn" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 7 or P1" /></Field>
          <Field id="ta" label="Area (optional)"><input id="ta" className="input" value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Poolside" /></Field>
        </div>
        <Field id="tc" label="Seats"><input id="tc" className="input" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))} /></Field>
        <Button variant="primary" type="submit" disabled={!name.trim()} loading={add.isPending}>Add table</Button>
      </form>
      {tables.isLoading ? (
        <Loading rows={2} />
      ) : (
        <div className="list">
          {(tables.data ?? []).map((t) => (
            <div key={t.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">Table {t.name}</span>
                <Button variant="ghost" disabled={!!t.openOrder} onClick={() => retire.mutate(t.id, { onError: report, onSuccess: () => toast.ok('Table removed') })}>Remove</Button>
              </div>
              <span className="small muted">{t.area ?? 'No area'} · seats {t.capacity}{t.openOrder ? ' · in use' : ''}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
